import UnifiedLightboxModal from '@components/unified-lightbox/Lightbox';
import {
	type VendorMediaKey,
	openUnifiedLightboxAtom,
} from '@components/unified-lightbox/atoms';
import { useAppSelector } from '@redux/hooks';
import { CtaButton } from '@tkww/orion-web-ds-action';
import { PageContainer } from '@tkww/orion-web-ds-page-layout';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { TextTitle } from '@tkww/orion-web-ds-text';
import { useSetAtom } from 'jotai';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import React, { type FC, useState } from 'react';
import type { Decorated } from 'types/vendor';
import SingleLevel from '../../../../../client/components/shared/Breadcrumbs/components/SingleLevel';
import { afterNextPaint } from '../../../../../helpers/after-next-paint';
import { composeStorefrontCanonicalUrl } from '../../../../../helpers/seo';
import MetaContent from '../../../../components/shared/HelmetHeader/HelmetHeader';
import Media from '../../components/Media';
import CtaWrapper from '../../containers/CtaWrapper/CtaWrapper';
import Styles from './StorefrontPhotos.scss';

const StorefrontPhotos: FC = () => {
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const images = vendor.mediaSummary;
	const category = useAppSelector((state) => state.category);
	const [isUnifiedLightboxModalOpen, setIsUnifiedLightboxModalOpen] =
		useState<boolean>(false);
	const setOpenUnifiedLightbox = useSetAtom(openUnifiedLightboxAtom);

	const goToStorefront = () => {
		window.location.href = vendor.uriData.relativeUri;
	};

	const handleOpenUnifiedLightbox = (
		indexInAllMedia: number,
		filter: VendorMediaKey,
	) => {
		afterNextPaint(() => {
			setOpenUnifiedLightbox({ indexInAllMedia, filter });
			setIsUnifiedLightboxModalOpen(true);
		});
	};

	const level = {
		url: vendor.uriData.relativeUri,
		label: vendor.name,
		analytics: {},
	};

	return (
		<PageContainer data-testid="storefront-photos">
			{isUnifiedLightboxModalOpen && (
				<UnifiedLightboxModal
					entryPoint="StorefrontGallery"
					isModalOpen={isUnifiedLightboxModalOpen}
					closeCallback={() => setIsUnifiedLightboxModalOpen(false)}
				/>
			)}
			<MetaContent
				canonical={composeStorefrontCanonicalUrl(
					`${vendor.uriData.relativeUri}/photos`,
				)}
				description={`Browse the latest photos for ${
					vendor.name
				} in ${startCase(toLower(vendor.address.city))}, ${
					vendor.address.state
				} on the knot. View ${images?.media?.length} ${
					category.name
				} photos shared by ${vendor.name}`}
				title={`${vendor.name} Photos`}
			/>
			<GridRow>
				<GridColumn xs="12" lg="8">
					<div className={Styles.container}>
						<div className={Styles.breadcrumbWrapper}>
							<SingleLevel level={level} />
						</div>
						<div className={Styles.titleWrapper}>
							<TextTitle size="lg">{vendor.name}'s Photos</TextTitle>
							<div>
								<CtaButton
									data-testid="storefront-profile-button"
									styleType="secondary"
									size="md"
									onClick={goToStorefront}
								>
									View profile
								</CtaButton>
							</div>
						</div>
						<div className={Styles.images}>
							{images?.media?.map((image, index) => {
								const key = `image-${index}`;
								return (
									<div
										key={key}
										className={Styles.image}
										onClick={() => {
											handleOpenUnifiedLightbox(index, 'photos');
										}}
										onKeyDown={() => {
											handleOpenUnifiedLightbox(index, 'photos');
										}}
										data-testid="storefront-photos-media"
										data-mediatype={image.mediaType}
									>
										<Media
											compact
											lazy
											smartCrop
											mediaItem={image}
											width={400}
											height={300}
										/>
									</div>
								);
							})}
						</div>
					</div>
				</GridColumn>
				<GridColumn xs="12" lg="4" className={Styles.leadformWrapper}>
					<CtaWrapper />
				</GridColumn>
			</GridRow>
		</PageContainer>
	);
};

export default StorefrontPhotos;
